import { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/data/translations';
import { MapPin, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import SEOHead from '@/components/SEOHead';

const InteractiveMap = lazy(() => import('@/components/InteractiveMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FadeIn({ children, delay = 0, className = '' }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function ContactPage() {
  const { lang } = useLanguage();
  const txt = t.contact[lang];
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', brochure_requested: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, language: lang });
      if (res.data.success) setSuccess(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="min-h-screen bg-obsidian text-alpine-mist pt-28 pb-20">
      <SEOHead page="contact" />
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.25em] text-gold-muted/60 font-sans mb-6">{txt.overline}</p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <h1 className="font-serif text-4xl sm:text-5xl text-alpine-mist tracking-tight">{txt.title}</h1>
            </FadeIn>
            <FadeIn delay={0.3}>
              <p className="text-base text-alpine-mist/40 font-sans mt-6 max-w-md font-light leading-relaxed">
                {txt.subtitle}
              </p>
            </FadeIn>

            <FadeIn delay={0.45}>
              <div className="mt-16 space-y-6">
                <div className="flex items-start gap-4">
                  <Mail size={16} className="text-gold-muted/50 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-alpine-mist/30 font-sans mb-1">Email</p>
                    <a href="mailto:guillaume@immoconceptsuisse.com" className="text-sm text-alpine-mist/70 hover:text-gold-muted transition-colors font-sans">
                      guillaume@immoconceptsuisse.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={16} className="text-gold-muted/50 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-alpine-mist/30 font-sans mb-1">{lang === 'fr' ? 'T\u00E9l\u00E9phone' : 'Phone'}</p>
                    <a href="tel:+41795100855" className="text-sm text-alpine-mist/70 hover:text-gold-muted transition-colors font-sans">
                      +41 79 510 08 55
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={16} className="text-gold-muted/50 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-alpine-mist/30 font-sans mb-1">{lang === 'fr' ? 'Bien immobilier' : 'Property'}</p>
                    <p className="text-sm text-alpine-mist/70 font-sans">Chemin de Plampro 32</p>
                    <p className="text-sm text-alpine-mist/50 font-sans">1936 Médières / Verbier</p>
                    <p className="text-sm text-alpine-mist/40 font-sans">Commune de Val de Bagnes, Valais, Suisse</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={16} className="text-gold-muted/50 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-alpine-mist/30 font-sans mb-1">{lang === 'fr' ? 'Agence' : 'Agency'}</p>
                    <p className="text-sm text-alpine-mist/70 font-sans font-medium">Guillaume RIEME</p>
                    <p className="text-sm text-alpine-mist/50 font-sans">Immoconcept Rième</p>
                    <p className="text-sm text-alpine-mist/40 font-sans">Route de Berne 223, 1066</p>
                    <a href="https://immoconceptsuisse.com/" target="_blank" rel="noopener noreferrer" className="text-sm text-gold-muted/50 hover:text-gold-muted transition-colors font-sans">
                      immoconceptsuisse.com
                    </a>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.6}>
              <div className="mt-10">
                <Suspense fallback={<div className="h-[200px] bg-obsidian/50 animate-pulse rounded-sm" />}>
                  <InteractiveMap height="200px" variant="dark" data-testid="contact-map" />
                </Suspense>
              </div>
            </FadeIn>
          </div>

          <div>
            {success ? (
              <FadeIn>
                <div className="flex items-center justify-center h-full" data-testid="contact-success">
                  <div className="text-center">
                    <p className="font-serif text-2xl text-gold-muted mb-4">{lang === 'fr' ? 'Merci' : 'Thank You'}</p>
                    <p className="text-sm text-alpine-mist/50 font-sans">{txt.success}</p>
                  </div>
                </div>
              </FadeIn>
            ) : (
              <FadeIn delay={0.3}>
                <form onSubmit={handleSubmit} className="space-y-8 pt-8 lg:pt-16" data-testid="contact-form">
                  <div>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => update('name', e.target.value)}
                      placeholder={txt.name}
                      required
                      className="input-luxury"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => update('email', e.target.value)}
                      placeholder={txt.email}
                      required
                      className="input-luxury"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => update('phone', e.target.value)}
                      placeholder={txt.phone}
                      className="input-luxury"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <textarea
                      value={form.message}
                      onChange={e => update('message', e.target.value)}
                      placeholder={txt.message}
                      rows={4}
                      className="input-luxury resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={form.brochure_requested}
                      onChange={e => update('brochure_requested', e.target.checked)}
                      id="brochure-check"
                      data-testid="contact-brochure-check"
                      className="w-4 h-4 accent-gold-muted"
                    />
                    <label htmlFor="brochure-check" className="text-xs text-alpine-mist/40 font-sans tracking-wide cursor-pointer">
                      {txt.requestBrochure}
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="cta-luxury"
                  >
                    {loading ? '...' : txt.send}
                  </button>
                </form>
              </FadeIn>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
